import {
  ArrowDownward,
  ArrowUpward,
  CheckCircle,
  Close,
  Event,
  Image,
  RemoveCircle,
  Timer,
  VerticalAlignBottom,
  VerticalAlignTop,
  type SvgIconComponent,
} from '@mui/icons-material';
import { Box, Button, Paper, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { memo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, IconButtonTooltip } from '~/components/IconButton';
import { ProgressiveImage } from '../ProgressiveImage';
import { AuthorizedLink } from '../links';
import type { MoveDir } from './PlaylistContainer';
import { DurationCell, EndsAtCell, StartsAtCell, StatusCell } from './cells';
import type { PlaylistDesign__PlaylistItem as PlaylistItem } from './index.generated';
interface MoveButtonProps {
  disabled: boolean;
  Icon: SvgIconComponent;
  loading?: boolean;
  onClick: () => void;
  tooltip: string;
}

const MoveButton = memo(function MoveButton({ disabled, Icon, onClick, tooltip }: MoveButtonProps) {
  return (
    <IconButtonTooltip label={tooltip}>
      <Button
        aria-label={tooltip}
        color="primary"
        disabled={disabled}
        onClick={onClick}
        size="small"
        variant="contained"
        sx={{
          minWidth: 'auto',
          width: 32,
          height: 32,
        }}
      >
        <Icon fontSize="small" />
      </Button>
    </IconButtonTooltip>
  );
});

interface PlaylistItemDetailFieldProps {
  Icon: SvgIconComponent;
  label: string;
  value: ReactNode | null | undefined;
}

const PlaylistItemDetailField = memo(function PlaylistItemDetailField({
  Icon,
  label,
  value,
}: PlaylistItemDetailFieldProps) {
  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Icon sx={{ color: 'text.secondary', fontSize: 18 }} />
        <Typography
          variant="h6"
          sx={{
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '.1em',
            fontSize: '12px',
          }}
        >
          {label}
        </Typography>
      </Stack>
      <Box sx={{ mt: 1 }}>
        {value || (
          <Typography component="div" variant="body1">
            {'--'}
          </Typography>
        )}
      </Box>
    </Paper>
  );
});

interface PlaylistItemDetailProps {
  canEdit: boolean;
  item: PlaylistItem;
  moveItem: (id: number, where: MoveDir) => Promise<void>;
  moveFlags: {
    canMoveTop: boolean;
    canMoveUp: boolean;
    canMoveDown: boolean;
    canMoveBottom: boolean;
  };
  onClose: () => void;
  onRemove: (id: number) => void;
}

export const PlaylistItemDetail = ({
  canEdit,
  item,
  moveItem,
  moveFlags,
  onClose,
  onRemove,
}: PlaylistItemDetailProps) => {
  const { t } = useTranslation();
  const itemName = item.contentItem.name;

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        p: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography
            variant="h4"
            noWrap
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
            title={itemName}
          >
            {itemName}
          </Typography>
          <IconButton
            label={t('common.actions.close')}
            size="small"
            onClick={onClose}
            sx={{
              ml: 1,
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          alignItems={{ xs: 'flex-start', lg: 'center' }}
          justifyContent={{ xs: 'flex-start', lg: 'space-between' }}
          sx={{ gap: { xs: 1, lg: 0 } }}
        >
          <Stack direction="row" spacing={0.5} role="group">
            <MoveButton
              disabled={!canEdit || !moveFlags.canMoveTop}
              Icon={VerticalAlignTop}
              onClick={() => moveItem(item.id, 'top')}
              tooltip={t('components.playlist_item_detail.move_top_tooltip')}
            />
            <MoveButton
              disabled={!canEdit || !moveFlags.canMoveUp}
              Icon={ArrowUpward}
              onClick={() => moveItem(item.id, 'up')}
              tooltip={t('components.playlist_item_detail.move_up_tooltip')}
            />
            <MoveButton
              disabled={!canEdit || !moveFlags.canMoveDown}
              Icon={ArrowDownward}
              onClick={() => moveItem(item.id, 'down')}
              tooltip={t('components.playlist_item_detail.move_down_tooltip')}
            />
            <MoveButton
              disabled={!canEdit || !moveFlags.canMoveBottom}
              Icon={VerticalAlignBottom}
              onClick={() => moveItem(item.id, 'bottom')}
              tooltip={t('components.playlist_item_detail.move_bottom_tooltip')}
            />
          </Stack>

          <Button
            color="error"
            disabled={!canEdit}
            onClick={() => onRemove(item.id)}
            size="small"
            startIcon={<RemoveCircle />}
            variant="contained"
          >
            {t('common.actions.remove')}
          </Button>
        </Stack>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <PlaylistItemDetailField
            label={t('common.labels.thumbnail')}
            Icon={Image}
            value={
              <AuthorizedLink
                authorized={
                  !!(item.contentItem.source?.canUpdate.value || item.contentItem.canUpdate.value)
                }
                entity={item.contentItem.source || item.contentItem}
              >
                <ProgressiveImage
                  alt={itemName || 'Thumbnail'}
                  aspectRatio="16/9"
                  src={item.contentItem.thumbnailUri}
                  width="100%"
                />
              </AuthorizedLink>
            }
          />
          <PlaylistItemDetailField
            label={t('common.labels.duration')}
            Icon={Timer}
            value={<DurationCell readOnly={!canEdit} item={item} />}
          />
          <PlaylistItemDetailField
            label={t('components.playlist_item_detail.starts_label')}
            Icon={Event}
            value={<StartsAtCell readOnly={!canEdit} item={item} />}
          />
          <PlaylistItemDetailField
            label={t('components.playlist_item_detail.ends_label')}
            Icon={Event}
            value={<EndsAtCell readOnly={!canEdit} item={item} />}
          />
          <PlaylistItemDetailField
            label={t('common.labels.status')}
            Icon={CheckCircle}
            value={<StatusCell item={item} />}
          />
        </Box>
      </Box>
    </Box>
  );
};
